import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { defaultSize, InputBaseComponent } from '@backbase/ui-ang/base-classes';
import { DomAttributesService } from '@backbase/ui-ang/services';
import { addBBPopperOffset, isRtl } from '@backbase/ui-ang/util';
import { Options, preventOverflow } from '@popperjs/core';

/**
 * Configuration options for typeahead autocomplete functionality in search box.
 *
 * @remarks
 * Provides integration with ng-bootstrap's {@link NgbTypeahead} component to enable
 * autocomplete suggestions as the user types in the search field. All properties are optional
 * and have sensible defaults. The `ngbTypeahead` function is the only truly required property
 * for enabling typeahead functionality.
 *
 * @template T - The type of items returned by the typeahead search function
 *
 * @example
 *
 * ```typescript
 * const options: TypeaheadOptions<string> = {
 *   ngbTypeahead: (text$: Observable<string>) =>
 *     text$.pipe(
 *       debounceTime(300),
 *       map(term => term === '' ? [] : ['Option1', 'Option2'].filter(v => v.includes(term)))
 *     ),
 *   editable: true,
 *   focusFirst: true,
 *   showHint: true,
 *   placement: 'bottom',
 *   inputFormatter: (item: string) => item,
 *   resultFormatter: (item: string) => `<strong>${item}</strong>`
 * };
 * ```
 */
export interface TypeaheadOptions<T> {
  /**
   * Function that receives search input as an Observable of strings and returns
   * an Observable of matching items. This is the core search/filter logic that
   * determines what suggestions appear as the user types.
   */
  ngbTypeahead: (text: Observable<string>) => Observable<Array<T>>;
  /**
   * Whether the search input allows free text entry or restricts input to selected items only.
   * Defaults to true if not specified.
   */
  editable?: boolean;
  /**
   * Whether the first matching option should be automatically selected when dropdown opens.
   * Defaults to true if not specified.
   */
  focusFirst?: boolean;
  /**
   * Whether to display inline hints as the user types, showing completions as faded text.
   * Defaults to false if not specified.
   */
  showHint?: boolean;
  /**
   * Position of the dropdown suggestions relative to the input field.
   * Valid values: 'top', 'bottom', 'left', 'right', 'auto'. Defaults to 'bottom' if not specified.
   */
  placement?: string;
  /**
   * Function that formats the selected item for display in the input field after selection.
   * By default, the item is displayed as-is.
   */
  inputFormatter?: (item: T) => string;
  /**
   * Function that formats each suggestion item for display in the dropdown list.
   * By default, the item is displayed as-is. Can return HTML strings for rich formatting.
   */
  resultFormatter?: (item: T) => string;
  /**
   * Custom template for rendering individual suggestion items in the dropdown.
   * If provided, takes precedence over {@link resultFormatter}. Use for complex layouts
   * or dynamic content that requires template control.
   */
  resultTemplate?: TemplateRef<any>;
  /**
   * Callback function invoked when an item is selected from the dropdown.
   * Receives a {@link NgbTypeaheadSelectItemEvent} containing the selected item and other context.
   */
  selectItem?: (item: NgbTypeaheadSelectItemEvent) => void;
  /**
   * CSS classes to apply to the dropdown container element.
   * Useful for styling, sizing, or adding custom behavior classes to the suggestions list.
   */
  groupCssClasses?: string;
}

const SIZE_MEDIUM = defaultSize;
const SIZE_SMALL = SIZE_MEDIUM / 2;

const KEYWORD_SMALL = 'sm';
const KEYWORD_MEDIUM = 'md';

/**
 * A reusable search input component with optional typeahead autocomplete and action buttons.
 *
 * @remarks
 * This component provides a search interface with configurable features including:
 * - **Typeahead Autocomplete**: Optional suggestions as users type, powered by ng-bootstrap
 * - **Action Buttons**: Optional search and clear buttons with configurable icons
 * - **Form Control Integration**: Implements `NG_VALUE_ACCESSOR` for two-way binding
 * - **Accessibility First**: Full ARIA functionality with customizable labels and descriptions
 * - **RTL**: Automatically adapts layout for right-to-left languages via {@link isRtl}
 * - **Responsive Sizing**: Allows 'sm' and 'md' size keywords with dynamic CSS classes
 *
 * Use this component when you need a flexible search interface that can work standalone or with
 * autocomplete suggestions. Combine with `typeaheadOptions` to enable suggestions, or use without
 * it for a simple search input.
 *
 * @template T - The type of items returned by typeahead autocomplete
 */
@Component({
  selector: 'bb-search-box-ui',
  templateUrl: './search-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SearchBoxComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SearchBoxComponent<T> extends InputBaseComponent implements AfterViewInit, DoCheck {
  /**
   * The placeholder text to display in the search input field when empty.
   *
   * @remarks
   * This text is shown as a hint to guide the user on what they can search for.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   placeholder="Search by name, email, or ID..."
   * />
   * ```
   */
  @Input() placeholder = '';
  /**
   * The maximum character length allowed in the search input. Defaults to 140 characters.
   *
   * @remarks
   * Enforced at the HTML `maxlength` attribute level. Users cannot enter more characters
   * than specified, and the input field respects this constraint.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   [maxLength]="50"
   * />
   * ```
   */
  @Input() maxLength = '140';
  /**
   * Configuration for typeahead autocomplete suggestions.
   *
   * @remarks
   * When provided, enables autocomplete dropdown with filtered suggestions as the user types.
   * Pass a {@link TypeaheadOptions} object configured with your search function and formatting preferences.
   * If not provided or undefined, the component renders a simple search input without suggestions.
   *
   * See {@link TypeaheadOptions} for detailed configuration options.
   */
  @Input() typeaheadOptions: TypeaheadOptions<T> | undefined;
  /**
   * Whether to display a search button icon before the input field. Defaults to false.
   *
   * @remarks
   * When true, displays a magnifying glass icon in a button that emits the {@link submit} event
   * when clicked. This is useful for explicit search submission. When false, search submission
   * can only be triggered by pressing Enter key or via the {@link onSubmit} method.
   *
   * Pair this with {@link searchLabel} to provide an accessible button label.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   [showSearch]="true"
   *   searchLabel="Find"
   *   (submit)="performSearch($event)"
   * />
   * ```
   */
  @Input() showSearch = false;
  /**
   * Whether to display a clear button icon that appears when the input has a value. Defaults to false.
   *
   * @remarks
   * When true and the input field contains text, a cancel icon button is displayed that clears
   * the search and emits the {@link clear} event. This button is only visible when there is content
   * and the component is not in readonly mode (see {@link isClearVisible}).
   *
   * Pair this with {@link clearLabel} to provide an accessible button label.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   [showClear]="true"
   *   clearLabel="Reset search"
   *   (clear)="onClearSearch()"
   * />
   * ```
   */
  @Input() showClear = false;
  /**
   * The ARIA label for the search button displayed when {@link showSearch} is true.
   *
   * @remarks
   * This label is crucial for accessibility. It describes the action of the search button
   * for screen reader users. Use clear, action-oriented text like "Search", "Find", or "Apply filter".
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   [showSearch]="true"
   *   searchLabel="Submit search query"
   * />
   * ```
   */
  @Input() searchLabel = $localize`:@@bb-search-box-ui.search-button.aria-label:Search`;
  /**
   * The ARIA label for the clear button displayed when {@link showClear} is true.
   *
   * @remarks
   * This label is crucial for accessibility. It describes the action of the clear button
   * for screen reader users. Use clear, action-oriented text like "Clear", "Reset", or "Remove text".
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   [showClear]="true"
   *   clearLabel="Clear search field"
   * />
   * ```
   */
  @Input() clearLabel = $localize`:@@bb-search-box-ui.clear-button.aria-label:Clear`;
  /**
   * The autocomplete attribute value for the underlying input element. Defaults to 'off'.
   *
   * @remarks
   * Controls browser autocomplete behavior. Set to 'off' to disable browser autocomplete,
   * or provide a valid autocomplete value like 'name', 'email', 'tel', 'address-line1', etc.
   * See HTML autocomplete specification for all valid values.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   autocomplete="email"
   * />
   * ```
   */
  @Input() autocomplete = 'off';
  /**
   * The ARIA role for the search container wrapper element. Defaults to 'search'.
   *
   * @remarks
   * Defines the semantic role of the container. Use 'search' for landmark search areas,
   * or customize with other role values if needed for different semantic contexts.
   * This helps screen readers and other assistive technologies identify the search region.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   containerRole="search"
   * />
   * ```
   */
  @Input() containerRole = 'search';
  /**
   * The ARIA label for the search container wrapper. Defaults to empty string.
   *
   * @remarks
   * When multiple search inputs exist on the same page, assign a unique label to each container
   * to help screen reader users distinguish between them. The label should be descriptive, like
   * "Global search" or "User directory search".
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   containerRole="search"
   *   containerAriaLabel="Global product search"
   * />
   * ```
   */
  @Input() containerAriaLabel = '';
  /**
   * Emits the current search input value when a search is submitted.
   *
   * @remarks
   * The event is triggered in two ways:
   * - When the user presses Enter in the search input field
   * - When the user clicks the search button (if {@link showSearch} is true)
   *
   * The emitted value is the current content of the input field at the time of submission.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   (submit)="performSearch($event)"
   * />
   * ```
   *
   * ```typescript
   * performSearch(searchTerm: string) {
   *   console.log('Searching for:', searchTerm);
   *   this.fetchResults(searchTerm);
   * }
   * ```
   */
  @Output() submit: EventEmitter<string | boolean | Object | null> = new EventEmitter<
    string | boolean | Object | null
  >();
  /**
   * Emits an event when the clear button is clicked.
   *
   * @remarks
   * Fired when the user clicks the clear button (visible only when {@link showClear} is true
   * and the input has content). The component automatically clears the input value before
   * emitting this event, and focuses the input field for continued interaction.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   [showClear]="true"
   *   (clear)="onClearSearch()"
   * />
   * ```
   *
   * ```typescript
   * onClearSearch() {
   *   this.filters = [];
   *   this.results = [];
   * }
   * ```
   */
  @Output() clear: EventEmitter<string> = new EventEmitter<string>();
  /**
   * Emits the updated value every time the user modifies the search input.
   *
   * @remarks
   * Fired on every keystroke or programmatic change to the input value via two-way binding.
   * Use this event for real-time validation, character count updates, or triggering live search
   * before the user explicitly submits. This event fires even if the value is empty.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   (valueChange)="onSearchChange($event)"
   * />
   * ```
   *
   * ```typescript
   * onSearchChange(value: string) {
   *   if (value.length >= 3) {
   *     this.liveSearch(value);
   *   }
   * }
   * ```
   */
  @Output() valueChange: EventEmitter<any> = this._valueChange;

  @ViewChild('searchBox', { static: true }) searchBox: ElementRef | undefined;
  @ViewChild('inputField') inputField: ElementRef | undefined;
  @ViewChild(NgbTypeahead) ngbTypeahead: NgbTypeahead | undefined;

  readonly labelId = `bb_searchbox_label_${this.id}`;
  readonly isRtl = isRtl;

  hasAppendContent = true;

  constructor(
    protected readonly cd: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly domAttrService: DomAttributesService,
    private readonly elem: ElementRef,
  ) {
    super(cd);
  }

  /**
   * Emits the current search input value as a submission event.
   *
   * @remarks
   * This method is called when the user presses Enter in the search field or clicks the search button.
   * It emits the {@link submit} event with the current input value. Use the {@link submit} event
   * listener in your component to handle the search action.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   (submit)="handleSubmit($event)"
   *   (keyup.enter)="searchComponent.onSubmit()"
   * />
   * ```
   */
  onSubmit() {
    this.submit.emit(this.value);
  }

  ngAfterViewInit() {
    this.domAttrService.moveAriaAttributes(this.elem.nativeElement, this.inputField?.nativeElement, this.renderer);

    if (this.inputField !== undefined) {
      this.inputField.nativeElement.removeAttribute('aria-multiline');
    }

    this.cd.detectChanges();
  }

  ngDoCheck() {
    if (this.searchBox) {
      const listBoxElem = this.searchBox.nativeElement?.querySelector('[role="listbox"]:not([aria-labelledby])');

      if (listBoxElem && this.label) {
        this.renderer.setAttribute(listBoxElem, 'aria-labelledby', this.labelId);
      }

      this.cd.detectChanges();
    }
  }

  /**
   * Clears the search input and emits the {@link clear} event.
   *
   * @remarks
   * Empties the input field and focuses it for continued user interaction.
   * This method is called when the user clicks the clear button. You can also call this
   * programmatically to reset the search from your component.
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   #searchBox
   * />
   * <button (click)="searchBox.onClear()">Reset All</button>
   * ```
   */
  onClear() {
    this.value = '';
    if (this.inputField !== undefined) {
      this.inputField.nativeElement.focus();
    }
    this.clear.emit();
  }

  /**
   * Sets focus to the search input field.
   *
   * @remarks
   * Programmatically moves keyboard focus to the input element. Useful when you want to
   * direct user attention to the search field or after certain user actions. The native
   * `focus()` method is called on the underlying input element.
   *
   * @example
   *
   * ```typescript
   * @ViewChild(SearchBoxComponent) searchBox!: SearchBoxComponent;
   *
   * openSearch() {
   *   this.searchBox.focusOnInputField();
   * }
   * ```
   */
  focusOnInputField() {
    if (this.inputField !== undefined) {
      this.inputField.nativeElement.focus();
    }
  }

  /**
   * Handles the focus event on the search input.
   *
   * @remarks
   * Adds the 'bb-focus' CSS class to the container to visually indicate focus state,
   * and calls the parent class focus handler for input management. This is triggered
   * when the user clicks on or tabs into the input field.
   *
   * @param event - The native FocusEvent from the input element
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   (focus)="onInputFocus($event)"
   * />
   * ```
   */
  onFocus(event: FocusEvent) {
    if (this.searchBox === undefined) {
      return;
    }
    this.searchBox.nativeElement.classList.add('bb-focus');
    super.onFocus(event);
  }

  /**
   * Handles the blur event when the search input loses focus.
   *
   * @remarks
   * Removes the 'bb-focus' CSS class from the container to update the visual state,
   * and calls the parent class blur handler for input management. This is triggered
   * when the user clicks away from or tabs out of the input field.
   *
   * @param event - The native FocusEvent from the input element
   *
   * @example
   *
   * ```html
   * <bb-search-box-ui
   *   (blur)="onInputBlur($event)"
   * />
   * ```
   */
  onBlur(event: FocusEvent) {
    if (this.searchBox === undefined) {
      return;
    }
    this.searchBox.nativeElement.classList.remove('bb-focus');
    super.onBlur(event);
  }

  /**
   * Determines whether the clear button should be visible.
   *
   * @remarks
   * Returns true only when all conditions are met:
   * - {@link showClear} is true
   * - The input field has content (truthy value)
   * - The component is not in readonly mode
   *
   * The clear button is automatically hidden when the input is empty or the component
   * is readonly, even if {@link showClear} is true.
   *
   * @returns true if the clear button should be displayed, false otherwise
   *
   * @example
   *
   * ```html
   * @if (isClearVisible()) {
   *   <button>Clear</button>
   * }
   * ```
   */
  isClearVisible() {
    return this.showClear && !!this.value && !this.readonly;
  }

  /**
   * Sets focus to the editable wrapper element.
   *
   * @remarks
   * This method provides a way to focus the container element itself rather than the
   * input field. Useful for custom focus management or when you want to focus the
   * component's wrapper for accessibility purposes.
   *
   * @example
   *
   * ```typescript
   * @ViewChild(SearchBoxComponent) searchBox!: SearchBoxComponent;
   *
   * focusSearchContainer() {
   *   this.searchBox.focusEditableElement();
   * }
   * ```
   */
  focusEditableElement() {
    if (this.searchBox) {
      this.searchBox.nativeElement.focus();
    }
  }

  /**
   * Converts a numeric size value to a CSS size keyword.
   *
   * @remarks
   * Maps numeric size values to predefined keyword classes for responsive styling.
   * Sizes <= half of the default medium size are mapped to 'sm', otherwise to 'md'.
   * These keywords are applied as CSS classes (e.g., 'bb-search-box--sm') to control
   * component styling and icon sizing.
   *
   * @param size - The numeric size value to convert
   * @returns The CSS keyword ('sm' or 'md') corresponding to the provided size
   *
   * @example
   *
   * ```typescript
   * getKeywordBySize(150) // Returns 'md' (assuming default medium size > 150)
   * getKeywordBySize(75)  // Returns 'sm' (assuming this is <= half of default medium)
   * ```
   */
  getKeywordBySize(size: number | string): string {
    if (Number(size) <= SIZE_SMALL) {
      return KEYWORD_SMALL;
    } else {
      return KEYWORD_MEDIUM;
    }
  }

  /**
   * Converts a CSS size keyword to a numeric size value.
   *
   * @remarks
   * Reverses the mapping created by {@link getKeywordBySize}. Accepts keywords like 'sm' and 'md'
   * and returns the corresponding numeric values. Returns undefined if the keyword is not recognized.
   * This is useful for dynamic sizing calculations or determining actual pixel values from keyword inputs.
   *
   * @param keyword - The size keyword ('sm' or 'md') to convert
   * @returns The numeric size value if keyword is valid, undefined if not recognized
   *
   * @example
   *
   * ```typescript
   * getSizeByKeyword('md') // Returns defaultSize
   * getSizeByKeyword('sm') // Returns defaultSize / 2
   * getSizeByKeyword('invalid') // Returns undefined
   * ```
   */
  getSizeByKeyword(keyword: any): number | undefined {
    const sizeKeywords: { [index: string]: number } = {
      [KEYWORD_SMALL]: SIZE_SMALL,
      [KEYWORD_MEDIUM]: SIZE_MEDIUM,
    };

    return typeof keyword === 'string' && sizeKeywords.hasOwnProperty(keyword) ? sizeKeywords[keyword] : undefined;
  }

  /**
   * Handles selection of an item from the typeahead dropdown.
   *
   * @remarks
   * Delegates to the `selectItem` callback configured in {@link typeaheadOptions} if provided.
   * This method is called automatically by the ng-bootstrap typeahead component when a user
   * selects a suggestion from the dropdown list. Custom selection logic is handled via the
   * `selectItem` callback in your typeahead configuration.
   *
   * @param event - The {@link NgbTypeaheadSelectItemEvent} containing the selected item and metadata
   *
   * @example
   *
   * ```typescript
   * typeaheadOptions: TypeaheadOptions<User> = {
   *   ngbTypeahead: (text$: Observable<string>) => this.searchUsers(text$),
   *   selectItem: (event) => {
   *     console.log('Selected user:', event.item);
   *     this.selectedUser = event.item;
   *   }
   * };
   * ```
   */
  onSelect(event: any) {
    return this.typeaheadOptions?.selectItem?.(event);
  }

  /**
   * @internal
   */
  protected get placementTypeahead() {
    return this.typeaheadOptions?.placement || 'bottom';
  }

  /**
   * @internal
   *
   * @remarks
   * Provides popper.js configuration options with Backbase-specific offset
   * settings for optimal dropdown popup positioning.
   */
  protected get popperOptions() {
    return (options: Partial<Options>) => {
      return addBBPopperOffset()(
        (() => {
          options.modifiers!.push(preventOverflow, {
            enabled: true,
            name: 'preventOverflow',
            phase: 'main',
            fn: function () {},
          });

          return options;
        })(),
      );
    };
  }
}
